import { z } from 'zod';

import type { FileKind } from '../uploads.js';
import { idSchema, optionalText } from './common.js';

/*
 * Um anexo tem arquivo, título e descrição — e nada mais.
 *
 * Havia categoria, data do documento e data de vencimento. Tudo isso obrigava
 * quem só quer anexar a planta revisada a responder três perguntas de
 * catalogação antes de conseguir. O que o arquivo é dentro do processo, quando
 * importa, cabe no título e na descrição; e o formato já sai da extensão, sem
 * ninguém precisar informar.
 */

export const createDocumentBodySchema = z.object({
  mediaId: idSchema,
  title: z.string().trim().min(2, 'Informe um título').max(180),
  description: optionalText(1000),
});
export type CreateDocumentBody = z.infer<typeof createDocumentBodySchema>;

/**
 * O que a edição altera.
 *
 * `mediaId` presente troca o arquivo. A versão anterior não é apagada: fica
 * guardada e acessível — ver {@link ObraDocument.previousVersions}.
 */
export const updateDocumentBodySchema = z.object({
  title: z.string().trim().min(2, 'Informe um título').max(180).optional(),
  description: optionalText(1000),
  mediaId: idSchema.optional(),
});
export type UpdateDocumentBody = z.infer<typeof updateDocumentBodySchema>;

/** Uma versão anterior do anexo, depois de uma substituição. */
export interface ObraDocumentVersion {
  readonly id: string;
  readonly url: string;
  readonly originalName: string;
  readonly extension: string;
  readonly sizeBytes: number;
  readonly replacedAt: string;
  readonly uploadedBy: { id: string; name: string } | null;
}

/** O relatório em que o arquivo foi enviado, quando não entrou pela aba. */
export interface ObraDocumentReport {
  readonly id: string;
  readonly title: string;
  readonly visitDate: string;
}

export interface ObraDocument {
  readonly id: string;
  readonly title: string;
  readonly description: string | null;
  /** Vem da extensão, e é o que separa projeto de planilha na tela. */
  readonly kind: FileKind;
  readonly extension: string;
  readonly url: string;
  readonly originalName: string;
  readonly sizeBytes: number;
  readonly uploadedBy: { id: string; name: string } | null;
  readonly createdAt: string;
  /** Da mais recente para a mais antiga. Vazio quando nunca houve troca. */
  readonly previousVersions: readonly ObraDocumentVersion[];
  /**
   * Presente quando o arquivo foi enviado num relatório, e não pela aba.
   *
   * O dossiê lista esses arquivos para que ninguém precise abrir relatório
   * por relatório atrás de um laudo. Mas quem responde por eles é o
   * relatório: aqui eles se leem e se baixam, e não se editam nem se retiram.
   * Removido o relatório, somem daqui também (ADR 047).
   */
  readonly report: ObraDocumentReport | null;
}

export interface ObraDocumentGroup {
  readonly kind: FileKind;
  readonly items: readonly ObraDocument[];
}

export interface ObraDocumentsResult {
  /** Agrupados por tipo de arquivo, na ordem em que a tela os mostra. */
  readonly groups: readonly ObraDocumentGroup[];
  readonly total: number;
}
